import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  DEFAULT_PHONE_COUNTRY,
  type CountryCode,
  type PhoneCountryOption,
  applyInternationalTypedInput,
  buildE164FromNationalDisplay,
  digitsOnly,
  formatNationalDigits,
  formatPhoneForDocument,
  getPhoneCountryOptions,
  isValidE164Phone,
  parseE164ToNationalParts,
} from '@wapharma/shared';
import { IconChevronDown } from '@tabler/icons-react';
import { Input } from '../primitives/Input.js';
import { Popover } from '../primitives/Popover.js';
import { WP_ICON_STROKE } from '../icons/constants.js';
import { cx } from '../utils/cx.js';
import { PhoneFlag } from './PhoneFlag.js';
import styles from './PhoneInput.module.css';
import { focusAdjacent } from '../utils/focusTrap.js';
import { Pressable } from '../primitives/Pressable.js';

export type PhoneInputProps = {
  value: string;
  onChange: (e164: string) => void;
  defaultCountry?: CountryCode;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  hint?: string;
  /** When true (default), show inline error while digits are present but E.164 is not valid yet. */
  validateRealtime?: boolean;
  /**
   * When true, the blurred field shows document international form
   * (`+CC-…`) so issuers see what will bake into the PDF.
   */
  documentPreview?: boolean;
  /** BCP 47 locale for country names in the dial list. Default English. */
  countryLocale?: string;
  /** Fires when national digits exist and E.164 validity changes (empty field = valid). */
  onValidityChange?: (valid: boolean) => void;
};

export function PhoneInput({
  value,
  onChange,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  label = 'Téléphone',
  error,
  required,
  disabled,
  className,
  id,
  hint,
  validateRealtime = true,
  documentPreview = false,
  countryLocale = 'en',
  onValidityChange,
}: PhoneInputProps) {
  const phoneCountries = useMemo(
    () => getPhoneCountryOptions(countryLocale),
    [countryLocale],
  );
  const listId = useId();

  const [nationalFocused, setNationalFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);
  const [nationalText, setNationalText] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const prevExternalValueRef = useRef(value);
  const searchRef = useRef<HTMLInputElement>(null);
  const countryTriggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectedDialCode = useMemo(() => {
    const match = phoneCountries.find((o) => o.value === selectedCountry);
    return match ? `+${match.dialCode}` : '';
  }, [phoneCountries, selectedCountry]);

  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return phoneCountries;
    return phoneCountries.filter((o: PhoneCountryOption) => {
      const hay = `${o.label} ${o.dialCode} ${o.value}`.toLowerCase();
      return hay.includes(q) || hay.includes(q.replace(/^\+/, ''));
    });
  }, [phoneCountries, countryQuery]);

  useEffect(() => {
    if (value?.trim() || digitsOnly(nationalText)) return;
    setSelectedCountry(defaultCountry);
  }, [defaultCountry, nationalText, value]);

  useEffect(() => {
    if (nationalFocused) return;
    if (!value?.trim()) {
      const prev = prevExternalValueRef.current;
      prevExternalValueRef.current = value;
      if (prev?.trim()) {
        setNationalText('');
        setSelectedCountry(defaultCountry);
        return;
      }
      setNationalText((t) => (digitsOnly(t) ? t : ''));
      return;
    }

    prevExternalValueRef.current = value;
    const parts = parseE164ToNationalParts(value, defaultCountry);
    setSelectedCountry(parts.country);
    setNationalText(parts.nationalDisplay);
  }, [value, defaultCountry, nationalFocused]);

  const hasNationalDigits = digitsOnly(nationalText).length > 0;

  const currentE164 = useMemo(
    () => buildE164FromNationalDisplay(nationalText, selectedCountry),
    [nationalText, selectedCountry],
  );

  const realtimeError = useMemo(() => {
    if (!validateRealtime || !hasNationalDigits) return undefined;
    const candidate = currentE164 || value.trim();
    if (candidate && isValidE164Phone(candidate)) return undefined;
    return currentE164 || value.trim() ? 'Numéro invalide.' : 'Numéro incomplet ou invalide.';
  }, [validateRealtime, hasNationalDigits, currentE164, value]);

  const displayError = error ?? realtimeError;

  useEffect(() => {
    if (!onValidityChange) return;
    const candidate = currentE164 || value.trim();
    const valid = !hasNationalDigits || (!!candidate && isValidE164Phone(candidate));
    onValidityChange(valid);
  }, [hasNationalDigits, currentE164, value, onValidityChange]);

  useEffect(() => {
    if (!countryOpen) return;
    const idx = filteredCountries.findIndex((o) => o.value === selectedCountry);
    setActiveIndex(idx >= 0 ? idx : 0);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [countryOpen, filteredCountries, selectedCountry]);

  useEffect(() => {
    if (!countryOpen) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, countryOpen]);

  const applyCountry = useCallback(
    (next: CountryCode) => {
      setSelectedCountry(next);
      setCountryOpen(false);
      setCountryQuery('');
      const digits = digitsOnly(nationalText);
      if (!digits) {
        onChange('');
        return;
      }
      const formatted = formatNationalDigits(digits, next);
      setNationalText(formatted);
      const e164 = buildE164FromNationalDisplay(formatted, next);
      onChange(e164 || '');
    },
    [nationalText, onChange],
  );

  const handleNationalChange = (raw: string) => {
    const intl = applyInternationalTypedInput(raw, selectedCountry);
    if (intl) {
      setSelectedCountry(intl.country);
      setNationalText(intl.nationalDisplay);
      if (intl.e164) {
        onChange(intl.e164);
      } else if (!digitsOnly(intl.nationalDisplay)) {
        onChange('');
      }
      return;
    }

    const digits = digitsOnly(raw);
    if (!digits) {
      setNationalText('');
      onChange('');
      return;
    }
    const formatted = formatNationalDigits(digits, selectedCountry);
    setNationalText(formatted);
    const e164 = buildE164FromNationalDisplay(formatted, selectedCountry);
    if (e164) {
      onChange(e164);
    } else {
      onChange('');
    }
  };

  const handleNationalBlur = () => {
    setNationalFocused(false);
    const e164 = buildE164FromNationalDisplay(nationalText, selectedCountry);
    if (e164) {
      onChange(e164);
    } else if (!digitsOnly(nationalText)) {
      onChange('');
    }
  };

  const moveActive = (delta: number) => {
    if (!filteredCountries.length) return;
    setActiveIndex((prev) => (prev + delta + filteredCountries.length) % filteredCountries.length);
  };

  const handleListKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filteredCountries[activeIndex];
      if (opt) applyCountry(opt.value);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setCountryOpen(false);
      setCountryQuery('');
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const backwards = e.shiftKey;
      const trigger = countryTriggerRef.current;
      setCountryOpen(false);
      setCountryQuery('');
      if (trigger) queueMicrotask(() => focusAdjacent(trigger, backwards));
    }
  };

  const nationalId = id ? `${id}-national` : 'phone-national';
  const countryId = id ? `${id}-country` : 'phone-country';
  const errorId = `${nationalId}-error`;
  const listboxId = `${listId}-listbox`;

  const documentPreviewValue =
    documentPreview &&
    !nationalFocused &&
    value.trim() &&
    isValidE164Phone(value)
      ? formatPhoneForDocument(value)
      : null;
  const fieldValue = documentPreviewValue ?? nationalText;

  const countryTrigger = (
    <Pressable
      ref={countryTriggerRef}
      type="button"
      id={countryId}
      className={cx(
        styles.countryTrigger,
        displayError ? styles.countryTriggerError : undefined,
        disabled ? styles.countryTriggerDisabled : undefined,
        countryOpen ? styles.countryTriggerOpen : undefined,
      )}
      data-wp-phone-country
      aria-label={`Indicatif pays (${selectedDialCode})`}
      aria-haspopup="listbox"
      aria-expanded={countryOpen}
      aria-controls={countryOpen ? listboxId : undefined}
      aria-invalid={!!displayError}
      disabled={disabled}
    >
      <PhoneFlag country={selectedCountry} className={styles.countryFlag} />
      <span className={styles.countryDial} data-wp-phone-dial>
        {selectedDialCode}
      </span>
      <IconChevronDown className={styles.countryCaret} size={16} stroke={WP_ICON_STROKE} aria-hidden />
    </Pressable>
  );

  return (
    <div className={cx(styles.wrap, className)} data-wp-phone-input>
      {label ? (
        <label htmlFor={nationalId} className={styles.label}>
          {label}
          {required ? ' *' : ''}
        </label>
      ) : null}
      <div className={styles.row}>
        <Popover
          opened={countryOpen}
          onChange={(open) => {
            if (disabled) return;
            setCountryOpen(open);
            if (!open) setCountryQuery('');
          }}
          target={countryTrigger}
          portal
          align="start"
          portalMinWidth={320}
          portalMaxWidth={400}
          portalEstimatedHeight={320}
          portalZIndex="var(--wp-z-popover-over-modal)"
        >
          <div
            className={styles.countryPanel}
            data-wp-phone-country-panel
            onKeyDown={handleListKeyDown}
          >
            <input
              ref={searchRef}
              type="search"
              className={styles.countrySearch}
              placeholder="Search country or code…"
              value={countryQuery}
              onChange={(e) => {
                setCountryQuery(e.currentTarget.value);
                setActiveIndex(0);
              }}
              aria-label="Search country calling code"
              autoComplete="off"
            />
            <div
              id={listboxId}
              className={styles.countryList}
              role="listbox"
              aria-label="Country calling codes"
            >
              {filteredCountries.length === 0 ? (
                <div className={styles.countryEmpty} role="presentation">
                  No matches
                </div>
              ) : (
                filteredCountries.map((opt, i) => {
                  const active = opt.value === selectedCountry;
                  const highlighted = i === activeIndex;
                  return (
                    <Pressable
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      ref={(el) => {
                        optionRefs.current[i] = el;
                      }}
                      className={cx(
                        styles.countryOption,
                        active && styles.countryOptionActive,
                        highlighted && styles.countryOptionHighlight,
                      )}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => applyCountry(opt.value)}
                    >
                      <span className={styles.countryOptionMain}>
                        <PhoneFlag country={opt.value} className={styles.countryOptionFlag} />
                        <span className={styles.countryOptionName}>{opt.label.replace(/\s*\(\+\d+\)\s*$/, '')}</span>
                      </span>
                      <span className={styles.countryOptionDial}>+{opt.dialCode}</span>
                    </Pressable>
                  );
                })
              )}
            </div>
          </div>
        </Popover>
        <Input
          id={nationalId}
          className={styles.number}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          required={required}
          value={fieldValue}
          error={displayError}
          hint={hint}
          showErrorText={!label}
          aria-describedby={label && displayError ? errorId : undefined}
          onChange={(e) => handleNationalChange(e.currentTarget.value)}
          onFocus={() => setNationalFocused(true)}
          onBlur={handleNationalBlur}
        />
      </div>
      {label && displayError ? (
        <span id={errorId} className={styles.errorText} role="alert">
          {displayError}
        </span>
      ) : null}
    </div>
  );
}
